import React, { useState } from 'react';
import { ArrowUpIcon, ArrowDownIcon } from './Icons';

const LightningIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const FieldPickerModal = ({ isOpen, onClose, available, selected, onChange }) => {
  const [leftSelected, setLeftSelected] = useState([]);
  const [rightSelected, setRightSelected] = useState([]);
  const [customField, setCustomField] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  if (!isOpen) return null;
  
  const availableItems = available
    .filter(f => !selected.includes(f.id))
    .filter(f => 
      f.label.toLowerCase().includes(searchTerm.toLowerCase()) || 
      f.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  
  const move = (items, toRight) => {
    if (toRight) {
      onChange([...selected, ...items.map(i => i.id)]);
    } else {
      onChange(selected.filter(id => !items.includes(id)));
    }
    toRight ? setLeftSelected([]) : setRightSelected([]);
  };
  
  const moveOrder = (direction) => {
    if (rightSelected.length !== 1) return;
    const id = rightSelected[0];
    const idx = selected.indexOf(id);
    if (idx === -1) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= selected.length) return;
    const newSelected = [...selected];
    [newSelected[idx], newSelected[newIdx]] = [newSelected[newIdx], newSelected[idx]];
    onChange(newSelected);
  };
  
  const getLabel = (id) => {
    const found = available.find(a => a.id === id);
    return found ? `${found.label} (${found.id})` : id;
  };
  
  const addCustomField = () => {
    if (customField) {
      onChange([...selected, customField.toUpperCase()]);
      setCustomField('');
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl flex flex-col max-h-[90vh] border border-gray-700">
        <div className="p-4 border-b border-gray-700 bg-gray-900 flex justify-between items-center rounded-t-lg">
          <div className="flex items-center gap-2">
            <LightningIcon className="w-5 h-5 text-yellow-400" />
            <h3 className="font-bold text-white">Select Fields</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl font-bold px-2">×</button>
        </div>
        
        <div className="p-4 flex gap-4 h-96">
          {/* Available Fields */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex justify-between mb-1">
              <span className="text-xs font-bold text-gray-400 uppercase">Available</span>
              <button onClick={() => move(availableItems, true)} className="text-xs text-green-400 font-bold hover:underline">Add All</button>
            </div>
            <div className="mb-2">
              <input 
                type="text" 
                className="w-full border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none" 
                placeholder="Search fields..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
              />
            </div>
            <div className="flex-1 border border-gray-600 rounded overflow-y-auto mb-2 bg-gray-900">
              {availableItems.map(item => (
                <div 
                  key={item.id} 
                  onClick={() => setLeftSelected([item])} 
                  className={`px-3 py-2 text-xs cursor-pointer truncate ${leftSelected.includes(item) ? 'bg-yellow-500 text-gray-900' : 'text-gray-300 hover:bg-gray-700'}`} 
                  title={`${item.label} (${item.id})`}
                >
                  <span className="font-semibold">{item.label}</span> <span className="opacity-75">({item.id})</span>
                </div>
              ))}
            </div>
            <div className="flex gap-1">
              <input 
                className="flex-1 border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none" 
                placeholder="Custom Field ID..." 
                value={customField} 
                onChange={e => setCustomField(e.target.value)} 
              />
              <button onClick={addCustomField} className="bg-gray-600 border border-gray-500 px-3 rounded text-xs font-bold text-yellow-400 hover:bg-gray-500">+</button>
            </div>
          </div>
          
          {/* Move Buttons */}
          <div className="flex flex-col justify-center gap-2 shrink-0">
            <button 
              onClick={() => move(leftSelected, true)} 
              disabled={!leftSelected.length} 
              className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs disabled:opacity-50 font-bold text-yellow-400 hover:bg-gray-600"
            >
              &gt;
            </button>
            <button 
              onClick={() => move(rightSelected, false)} 
              disabled={!rightSelected.length} 
              className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs disabled:opacity-50 font-bold text-yellow-400 hover:bg-gray-600"
            >
              &lt;
            </button>
          </div>
          
          {/* Selected Fields */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex justify-between mb-1">
              <span className="text-xs font-bold text-gray-400 uppercase">Selected</span>
              <button onClick={() => onChange([])} className="text-xs text-red-400 font-bold hover:underline">Clear All</button>
            </div>
            <div className="flex-1 border border-gray-600 rounded overflow-y-auto bg-gray-900">
              {selected.map(id => (
                <div 
                  key={id} 
                  onClick={() => setRightSelected([id])} 
                  className={`px-3 py-2 text-xs cursor-pointer truncate ${rightSelected.includes(id) ? 'bg-yellow-500 text-gray-900' : 'text-gray-300 hover:bg-gray-700'}`} 
                  title={getLabel(id)}
                >
                  {getLabel(id)}
                </div>
              ))}
            </div>
          </div>
          
          {/* Order Buttons */}
          <div className="flex flex-col justify-center gap-2 shrink-0">
            <button 
              onClick={() => moveOrder('up')} 
              disabled={rightSelected.length !== 1} 
              className="p-2 border border-gray-600 rounded hover:bg-gray-700 disabled:opacity-50 text-gray-400"
            >
              <ArrowUpIcon />
            </button>
            <button 
              onClick={() => moveOrder('down')} 
              disabled={rightSelected.length !== 1} 
              className="p-2 border border-gray-600 rounded hover:bg-gray-700 disabled:opacity-50 text-gray-400"
            >
              <ArrowDownIcon />
            </button>
          </div>
        </div>
        
        <div className="p-4 border-t border-gray-700 bg-gray-900 flex justify-end rounded-b-lg">
          <button onClick={onClose} className="px-5 py-2 bg-yellow-500 text-gray-900 rounded font-medium shadow-sm hover:bg-yellow-400">
            ⚡ Apply Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default FieldPickerModal;